<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { pdfNotesChapterMap, pdfNotesChapters } from "~/features/computer/data/pdf-notes.chapters";

const route = useRoute();
const slug = Array.isArray(route.params.chapter) ? route.params.chapter[0] : route.params.chapter;
const chapter = slug ? pdfNotesChapterMap[slug] : null;

if (!chapter) {
  throw createError({
    statusCode: 404,
    statusMessage: "Chapter PDF page not found",
  });
}

const pdfSections = [
  { id: "overview", label: "Overview" },
  { id: "notes", label: "Full Notes" },
  { id: "solved-exercises", label: "Solved Exercises" },
  { id: "important-questions", label: "Important Questions" },
];

const relatedDownloads = pdfNotesChapters
  .filter((item) => item.slug !== chapter.slug)
  .slice(0, 2)
  .map((item) => ({
    title: `${item.chapterLabel} Notes PDF`,
    description: `${item.title} chapter ke printable notes aur solved revision blocks.`,
    to: `/11th/computer/notes-pdf/${item.slug}/`,
  }));

const extraDownloads = [
  ...relatedDownloads,
  {
    title: "Full Book Notes PDF",
    description: "Chapter-wise notes index jahan se puri book ka printable study flow khulta hai.",
    to: "/11th/computer/notes/",
  },
  {
    title: "Model Papers PDF",
    description: "Paper pattern aur final prep route ke liye exam-focused pages.",
    to: "/11th/computer/paper-pattern/",
  },
].slice(0, 3);

function openPrintDialog() {
  if (import.meta.client) {
    window.print();
  }
}

usePageSeo({
  title: chapter.pageTitle,
  description: chapter.description,
  keywords: chapter.keywords,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Notes PDF", item: "/11th/computer/notes-pdf/" },
    { name: `${chapter.chapterLabel} ${chapter.title}`, item: `/11th/computer/notes-pdf/${chapter.slug}/` },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[28px] border border-stone-200 bg-gradient-to-br from-stone-50 via-white to-sky-50 px-5 py-6 shadow-sm md:px-8 md:py-8 print:hidden">
      <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-700">{{ chapter.chapterLabel }} - Class 11 Computer</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">{{ chapter.chapterLabel }} Computer Notes (PDF)</h1>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-700 md:text-base">
        {{ chapter.intro }}
      </p>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#pdf-viewer"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          View PDF
        </a>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700"
          @click="openPrintDialog"
        >
          Download PDF
        </button>
      </div>

      <nav aria-label="PDF sections" class="mt-5 flex flex-wrap gap-2">
        <a
          v-for="section in pdfSections"
          :key="section.id"
          :href="`#${section.id}`"
          class="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-sky-300 hover:text-sky-700"
        >
          {{ section.label }}
        </a>
      </nav>
    </section>

    <section class="print:pt-0">
      <div
        id="pdf-viewer"
        class="mx-auto max-w-[700px] rounded-[28px] border border-stone-200 bg-white px-6 py-8 shadow-[0_18px_60px_rgba(15,23,42,0.08)] md:px-12 md:py-12 print:max-w-none print:rounded-none print:border-0 print:px-0 print:py-0 print:shadow-none"
      >
        <div class="border-b border-dashed border-stone-300 pb-6">
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Printable Notes</p>
          <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950">{{ chapter.pageTitle }}</h2>
          <p class="mt-3 text-sm leading-7 text-slate-700">
            Yeh page {{ chapter.title }} chapter ko document format me organize karta hai. Is me notes, solved exercises aur important questions aik printable flow me diye gaye hain taake student bina extra clicks ke revise kar sake.
          </p>
        </div>

        <div id="overview" class="mt-8">
          <h2 class="text-2xl font-semibold tracking-tight text-slate-950">{{ chapter.chapterLabel }} Overview</h2>
          <p class="mt-3 text-base leading-8 text-slate-700">
            {{ chapter.overview }}
          </p>
        </div>

        <div id="notes" class="mt-10 space-y-8">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-950">Full Notes</h2>
            <p class="mt-3 text-base leading-8 text-slate-700">
              {{ chapter.fullNotesIntro }}
            </p>
          </div>

          <section
            v-for="item in chapter.noteBlocks"
            :key="item.title"
            class="rounded-[22px] border border-stone-200 bg-stone-50 px-5 py-5"
          >
            <h3 class="text-xl font-semibold text-slate-950">{{ item.title }}</h3>
            <p class="mt-3 text-base leading-8 text-slate-700">{{ item.body }}</p>
          </section>

          <section class="rounded-[22px] border border-stone-200 bg-white px-5 py-5">
            <h3 class="text-xl font-semibold text-slate-950">Key Points</h3>
            <ol class="mt-4 space-y-3 text-base leading-8 text-slate-700">
              <li v-for="(point, index) in chapter.keyPoints" :key="point">
                {{ index + 1 }}. {{ point }}
              </li>
            </ol>
          </section>

          <section
            v-if="chapter.conceptCards?.length"
            class="rounded-[22px] border border-stone-200 bg-white px-5 py-5"
          >
            <h3 class="text-xl font-semibold text-slate-950">Core Concepts</h3>
            <div class="mt-4 grid gap-4 md:grid-cols-2">
              <div
                v-for="item in chapter.conceptCards"
                :key="item.title"
                class="rounded-[20px] border border-stone-200 bg-stone-50 p-4"
              >
                <h4 class="text-lg font-semibold text-slate-950">{{ item.title }}</h4>
                <p class="mt-2 text-base leading-8 text-slate-700">{{ item.detail }}</p>
              </div>
            </div>
          </section>
        </div>

        <div id="solved-exercises" class="mt-10 space-y-6">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-950">Solved Exercises</h2>
            <p class="mt-3 text-base leading-8 text-slate-700">
              {{ chapter.solvedExercisesIntro }}
            </p>
          </div>

          <section class="rounded-[22px] border border-stone-200 bg-stone-50 px-5 py-5">
            <h3 class="text-xl font-semibold text-slate-950">Quick MCQs</h3>
            <div class="mt-4 space-y-4">
              <article
                v-for="question in chapter.mcqs.slice(0, 4)"
                :key="question.id"
                class="rounded-[18px] border border-stone-200 bg-white p-4"
              >
                <p class="text-base font-semibold leading-7 text-slate-950">{{ question.question }}</p>
                <p class="mt-2 text-sm text-slate-600">
                  Correct answer: <span class="font-semibold text-slate-900">{{ question.options[question.correctIndex] }}</span>
                </p>
                <p class="mt-2 text-base leading-8 text-slate-700">{{ question.explanation }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-[22px] border border-stone-200 bg-white px-5 py-5">
            <h3 class="text-xl font-semibold text-slate-950">Long Answer Structure</h3>
            <div class="mt-4 space-y-4">
              <article
                v-for="question in chapter.longAnswers"
                :key="question.id"
                class="rounded-[18px] border border-stone-200 bg-stone-50 p-4"
              >
                <h4 class="text-lg font-semibold text-slate-950">{{ question.question }}</h4>
                <ol v-if="question.steps?.length" class="mt-3 space-y-2 text-base leading-8 text-slate-700">
                  <li v-for="(step, index) in question.steps" :key="`${question.id}-${index}`">
                    {{ index + 1 }}. {{ step }}
                  </li>
                </ol>
                <p class="mt-3 text-base leading-8 text-slate-700">{{ question.solution }}</p>
              </article>
            </div>
          </section>
        </div>

        <div id="important-questions" class="mt-10 space-y-6">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-950">Important Questions</h2>
            <p class="mt-3 text-base leading-8 text-slate-700">
              {{ chapter.importantQuestionsIntro }}
            </p>
          </div>

          <section class="space-y-4">
            <article
              v-for="question in chapter.shortQuestions"
              :key="question.id"
              class="rounded-[22px] border border-stone-200 bg-white px-5 py-5"
            >
              <h3 class="text-lg font-semibold text-slate-950">{{ question.question }}</h3>
              <p class="mt-3 text-base leading-8 text-slate-700">{{ question.answer }}</p>
            </article>
          </section>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 md:px-8 print:hidden">
      <h2 class="text-2xl font-semibold tracking-tight text-slate-950">Open Related Pages</h2>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="link in chapter.links.slice(0, 3)"
          :key="link.to"
          :to="link.to"
          class="rounded-[22px] border border-stone-200 bg-white p-5 transition hover:border-sky-300 hover:shadow-sm"
        >
          <h3 class="text-lg font-semibold text-slate-950">{{ link.label }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-700">{{ chapter.title }} chapter ka related route open karein.</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 md:px-8 print:hidden">
      <h2 class="text-2xl font-semibold tracking-tight text-slate-950">Also Download</h2>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in extraDownloads"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-stone-200 bg-white p-5 transition hover:border-sky-300 hover:shadow-sm"
        >
          <h3 class="text-lg font-semibold text-slate-950">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-700">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>
  </PageContainer>
</template>
